import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  Skeleton,
  useTheme,
  alpha,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { format, parseISO } from 'date-fns';

interface PerformanceTrendChartProps {
  data: Array<{
    date: string;
    adr?: number;
    occupancy?: number;
    revpar?: number;
  }>;
  loading?: boolean;
  title?: string;
  timeRange?: '1_year' | '10_year' | '20_year';
  onTimeRangeChange?: (range: '1_year' | '10_year' | '20_year') => void;
  allClassificationsData?: Array<{
    date: string;
    slice: string;
    adr?: number;
    occupancy?: number;
    revpar?: number;
  }>;
}

type MetricType = 'all' | 'adr' | 'occupancy' | 'revpar';

export const PerformanceTrendChart: React.FC<PerformanceTrendChartProps> = ({
  data,
  loading = false,
  title = 'Performance Trends',
  timeRange = '1_year',
  onTimeRangeChange,
  allClassificationsData,
}) => {
  const theme = useTheme();
  const [selectedMetric, setSelectedMetric] = useState<MetricType>('all');
  const [hoveredClassification, setHoveredClassification] = useState<string | null>(null);

  const handleMetricChange = (
    _event: React.MouseEvent<HTMLElement>,
    newMetric: MetricType | null
  ) => {
    if (newMetric !== null) {
      setSelectedMetric(newMetric);
    }
  };

  // Process data based on selected metric
  const chartData = React.useMemo(() => {
    // If showing a specific metric and we have all classifications data, use that
    if (selectedMetric !== 'all' && allClassificationsData && allClassificationsData.length > 0) {
      // Group by date and organize by classification
      const groupedData: Record<string, any> = {};
      
      allClassificationsData.forEach(item => {
        const dateFormatted = format(parseISO(item.date), timeRange === '20_year' ? 'yyyy' : 'MMM yyyy');
        
        if (!groupedData[item.date]) {
          groupedData[item.date] = {
            date: item.date,
            dateFormatted,
          };
        }
        
        // Add classification-specific data
        if (item.slice && selectedMetric) {
          const key = `${item.slice}_${selectedMetric}`;
          groupedData[item.date][key] = item[selectedMetric as keyof typeof item];
        }
      });
      
      return Object.values(groupedData).sort((a, b) => 
        new Date(a.date).getTime() - new Date(b.date).getTime()
      );
    } else {
      // Use regular data for "all" metrics view
      return data.map((point) => ({
        ...point,
        dateFormatted: format(parseISO(point.date), timeRange === '20_year' ? 'yyyy' : 'MMM yyyy'),
      }));
    }
  }, [data, allClassificationsData, selectedMetric, timeRange]);

  // Get unique classifications with specific ordering
  const classifications = React.useMemo(() => {
    if (!allClassificationsData) return [];
    const uniqueSlices = new Set(allClassificationsData.map(item => item.slice).filter(Boolean));
    const classOrder = ['Luxury', 'Upper Upscale', 'Upscale', 'Upper Midscale', 'Midscale', 'Economy'];

    // Sort by the defined order, with any other classifications at the end
    return Array.from(uniqueSlices).sort((a, b) => {
      const aIndex = classOrder.indexOf(a);
      const bIndex = classOrder.indexOf(b);

      // If both are in the defined order, sort by that order
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      // If only one is in the defined order, it comes first
      if (aIndex !== -1) return -1;
      if (bIndex !== -1) return 1;
      // Otherwise, sort alphabetically
      return a.localeCompare(b);
    });
  }, [allClassificationsData]);

  // Color palette for classifications
  const classificationColors: Record<string, string> = {
    'All': '#a6a6a6',           // RGB(166,166,166)
    'Luxury': '#003f9f',         // RGB(0,63,159)
    'Upper Upscale': '#16d9bc',  // RGB(22,217,188)
    'Upscale': '#0c7876',        // RGB(12,120,118)
    'Upper Midscale': '#32a8fd', // RGB(50,168,253)
    'Midscale': '#ea80ff',       // RGB(234,128,255)
    'Economy': '#7b2a5c',        // RGB(123,42,92)
    'Unclassified': '#808080'    // RGB(128,128,128)
  };

  // Helper to get y-axis configuration based on selected metric
  const getYAxisConfig = () => {
    if (selectedMetric === 'occupancy') {
      return {
        leftLabel: '',
        rightLabel: 'Occupancy (%)',
        showLeftAxis: false,
        showRightAxis: true,
      };
    } else if (selectedMetric === 'all') {
      return {
        leftLabel: 'ADR ($) / RevPAR ($)',
        rightLabel: 'Occupancy (%)',
        showLeftAxis: true,
        showRightAxis: true,
      };
    } else {
      return {
        leftLabel: selectedMetric === 'adr' ? 'ADR ($)' : 'RevPAR ($)',
        rightLabel: '',
        showLeftAxis: true,
        showRightAxis: false,
      };
    }
  };

  const yAxisConfig = getYAxisConfig();

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <Box
          sx={{
            backgroundColor: 'background.paper',
            p: 2,
            border: 1,
            borderColor: 'divider',
            borderRadius: 1,
            boxShadow: 2,
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            {label}
          </Typography>
          {payload.map((entry: any, index: number) => {
            if (entry.value === undefined || entry.value === null) return null;
            
            // Format based on metric type
            const isOccupancy = entry.name === 'Occupancy' || 
              (selectedMetric === 'occupancy' && entry.dataKey?.includes('_occupancy'));
            const formattedValue = isOccupancy
              ? `${entry.value.toFixed(1)}%`
              : `$${entry.value.toFixed(2)}`;
            
            return (
              <Typography
                key={index}
                variant="body2"
                sx={{ color: entry.color }}
              >
                {entry.name}: {formattedValue}
              </Typography>
            );
          }).filter(Boolean)}
        </Box>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Skeleton variant="text" width="40%" height={32} />
          <Skeleton variant="rectangular" width="100%" height={400} sx={{ mt: 3 }} />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardContent>
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              {title}
            </Typography>
            
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {onTimeRangeChange && (
                <ToggleButtonGroup
                  value={timeRange}
                  exclusive
                  onChange={(e, newValue) => newValue && onTimeRangeChange(newValue)}
                  size="small"
                >
                  <ToggleButton value="1_year">1 Year</ToggleButton>
                  <ToggleButton value="10_year">10 Years</ToggleButton>
                  <ToggleButton value="20_year">20 Years</ToggleButton>
                </ToggleButtonGroup>
              )}
              
              <ToggleButtonGroup
                value={selectedMetric}
                exclusive
                onChange={handleMetricChange}
                size="small"
              >
                <ToggleButton value="all">All Metrics</ToggleButton>
                <ToggleButton value="adr">ADR</ToggleButton>
                <ToggleButton value="occupancy">Occupancy</ToggleButton>
                <ToggleButton value="revpar">RevPAR</ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
          
          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              display: 'block', 
              mt: 1, 
              textAlign: 'right',
              fontStyle: 'italic'
            }}
          >
            {selectedMetric === 'all' 
              ? 'Select a specific metric to see data broken out by hotel classification'
              : `Showing ${selectedMetric.toUpperCase()} broken out by hotel classification`
            }
          </Typography>
        </Box>

        <Box sx={{ width: '100%', height: 400 }}>
          <ResponsiveContainer>
            <LineChart
              data={chartData}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid
                strokeDasharray="3 3"
                stroke={alpha(theme.palette.divider, 0.5)}
                vertical={timeRange !== '10_year' && timeRange !== '20_year'}
              />

              {/* Add vertical lines only at January dates for 10/20 year views */}
              {(timeRange === '10_year' || timeRange === '20_year') &&
                chartData
                  .filter(d => {
                    try {
                      return format(parseISO(d.date), 'MM') === '01';
                    } catch {
                      return false;
                    }
                  })
                  .map(d => (
                    <ReferenceLine
                      key={d.date}
                      x={d.date}
                      yAxisId={yAxisConfig.showLeftAxis ? "left" : "right"}
                      stroke={alpha(theme.palette.divider, 0.5)}
                      strokeDasharray="3 3"
                    />
                  ))
              }
              <XAxis
                dataKey="date"
                tick={{ fontSize: 12 }}
                stroke={theme.palette.text.secondary}
                tickFormatter={(value) => {
                  try {
                    const date = parseISO(value);
                    // For 10-year or 20-year views, only show January years
                    if (timeRange === '10_year' || timeRange === '20_year') {
                      if (format(date, 'MM') === '01') {
                        return format(date, 'yyyy');
                      }
                      return '';
                    }
                    // For 1-year view, show month and year
                    return format(date, 'MMM yyyy');
                  } catch {
                    return value;
                  }
                }}
                interval={timeRange === '10_year' || timeRange === '20_year' ? 0 : 'preserveStartEnd'}
              />
              {yAxisConfig.showLeftAxis && (
                <YAxis
                  yAxisId="left"
                  tick={{ fontSize: 12 }}
                  stroke={theme.palette.text.secondary}
                  label={{
                    value: yAxisConfig.leftLabel,
                    angle: -90,
                    position: 'insideLeft',
                    style: { fontSize: 12 },
                  }}
                />
              )}
              {yAxisConfig.showRightAxis && (
                <YAxis
                  yAxisId="right"
                  orientation="right"
                  tick={{ fontSize: 12 }}
                  stroke={theme.palette.text.secondary}
                  label={{
                    value: yAxisConfig.rightLabel,
                    angle: 90,
                    position: 'insideRight',
                    style: { fontSize: 12 },
                  }}
                />
              )}
              <Tooltip content={<CustomTooltip />} />
              <Legend
                wrapperStyle={{ fontSize: 12 }}
                iconType="line"
                onMouseEnter={(e: any) => {
                  if (e && e.dataKey) {
                    setHoveredClassification(e.value || e.dataKey);
                  }
                }}
                onMouseLeave={() => {
                  setHoveredClassification(null);
                }}
              />
              
              {/* Render lines based on selected metric */}
              {selectedMetric === 'all' ? (
                // Show all metrics for "All" classification only
                // Colors from /docs/store_apps/hotel-class-color-scheme.md
                <>
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="adr"
                    stroke="#0050ad" // RGB(0,80,173)
                    strokeWidth={hoveredClassification === 'ADR' ? 3 : 2}
                    strokeOpacity={hoveredClassification ? (hoveredClassification === 'ADR' ? 1 : 0.2) : 1}
                    dot={false}
                    name="ADR"
                    connectNulls={false}
                  />
                  <Line
                    yAxisId="right"
                    type="monotone"
                    dataKey="occupancy"
                    stroke="#f5a800" // RGB(245,168,0)
                    strokeWidth={hoveredClassification === 'Occupancy' ? 3 : 2}
                    strokeOpacity={hoveredClassification ? (hoveredClassification === 'Occupancy' ? 1 : 0.2) : 1}
                    dot={false}
                    name="Occupancy"
                    connectNulls={false}
                  />
                  <Line
                    yAxisId="left"
                    type="monotone"
                    dataKey="revpar"
                    stroke="#011e41" // RGB(1,30,65)
                    strokeWidth={hoveredClassification === 'RevPAR' ? 3 : 2}
                    strokeOpacity={hoveredClassification ? (hoveredClassification === 'RevPAR' ? 1 : 0.2) : 1}
                    dot={false}
                    name="RevPAR"
                    connectNulls={false}
                  />
                </>
              ) : (
                // Show selected metric for all classifications
                classifications.map(classification => {
                  const isHovered = hoveredClassification === classification;
                  const baseWidth = classification === 'All' ? 3 : 2;
                  const strokeWidth = isHovered ? baseWidth + 1 : baseWidth;
                  const strokeOpacity = hoveredClassification ? (isHovered ? 1 : 0.2) : 1;

                  return (
                    <Line
                      key={classification}
                      yAxisId={selectedMetric === 'occupancy' ? 'right' : 'left'}
                      type="monotone"
                      dataKey={`${classification}_${selectedMetric}`}
                      stroke={classificationColors[classification] || '#000'}
                      strokeWidth={strokeWidth}
                      strokeOpacity={strokeOpacity}
                      strokeDasharray={classification === 'All' ? '5 5' : undefined}
                      dot={false}
                      name={classification}
                      connectNulls={false}
                    />
                  );
                })
              )}
            </LineChart>
          </ResponsiveContainer>
        </Box>
      </CardContent>
    </Card>
  );
};